"use strict";
// Copyright (C) Microsoft Corporation. All rights reserved.
Object.defineProperty(exports, "__esModule", { value: true });
exports.isElementInValidFocusContext = exports.handleFocusContainerZIndexChange = exports.removeFocusContainer = exports.setContainerFocus = exports.addFocusContainer = exports.getFocusContext = exports.FocusContext = exports.hasActiveFocusContext = exports.addToPreviousContainers = exports.getPreviouslyFocusedContainers = exports.pruneActiveContainers = exports.getActiveContainers = exports.pruneFocusedContainers = exports.getFocusedContainers = void 0;
const trappable_1 = require("../../../components/trappable");
const utils_1 = require("../../utils");
const initialFocusFinder_1 = require("../../search/initialFocusFinder");
const focusManager_1 = require("../focusManager");
const dependencyGraph_1 = require("./dependencyGraph");
const types_1 = require("../../../types");
/*
 * Focused Containers: The containers that are focused
 */
let focusedContainers = [];
/**
 *
 * @returns a list of the focused FocusContainers
 */
const getFocusedContainers = () => {
    return focusedContainers;
};
exports.getFocusedContainers = getFocusedContainers;
/**
 * Prunes the focused containers that are no longer contained within the body
 */
const pruneFocusedContainers = () => {
    focusedContainers = focusedContainers.filter((container) => document.body.contains(container.getElement()));
};
exports.pruneFocusedContainers = pruneFocusedContainers;
/*
 * Active Containers: The containers that are focused and do not have any descendant focus containers that are focused
 */
let activeContainers = [];
/**
 *
 * @returns a list of the focused FocusContainers that do not have any descendant focus containers that are focused
 */
const getActiveContainers = () => {
    return activeContainers;
};
exports.getActiveContainers = getActiveContainers;
/**
 * Prunes the focused containers that are no longer contained within the body
 */
const pruneActiveContainers = () => {
    activeContainers = activeContainers.filter((container) => document.body.contains(container.getElement()));
};
exports.pruneActiveContainers = pruneActiveContainers;
/*
 * Previously Focused Containers: The most recent containers to contain current focus, ordered oldest to newest
 */
let previouslyFocusedContainers = [];
/**
 *
 * @returns a list of the previously focused containers, ordered oldest to newest
 */
const getPreviouslyFocusedContainers = () => previouslyFocusedContainers;
exports.getPreviouslyFocusedContainers = getPreviouslyFocusedContainers;
/**
 * Adds a containers as the most recent previous container
 *
 * @param container the container to add
 */
const addToPreviousContainers = (container) => {
    removeFromPreviousContainers(container);
    if (container.takesFocus() !== types_1.TakesFocus.None) {
        previouslyFocusedContainers.push(container);
    }
};
exports.addToPreviousContainers = addToPreviousContainers;
/**
 * Removes a containers from the previous container tracking
 *
 * @param container the container to remove
 */
const removeFromPreviousContainers = (container) => {
    previouslyFocusedContainers = previouslyFocusedContainers.filter((innerContainer) => innerContainer !== container);
};
/**
 * @returns whether there is any active focus context
 */
const hasActiveFocusContext = () => (0, exports.getFocusContext)() !== FocusContext.None;
exports.hasActiveFocusContext = hasActiveFocusContext;
var FocusContext;
(function (FocusContext) {
    FocusContext[FocusContext["None"] = 0] = "None";
    FocusContext[FocusContext["Global"] = 1] = "Global";
    FocusContext[FocusContext["Specific"] = 2] = "Specific";
})(FocusContext || (exports.FocusContext = FocusContext = {}));
/**
 * @returns the current focus context. A focus context is considered global if there are no active
 * focus containers but there is a registered BodyTrappable. A focus context is considered specific if
 * there is an active focus container. A focus context is considered none if there are no active focus
 * or BodyTrappables.
 */
const getFocusContext = () => {
    if (activeContainers.length !== 0) {
        return FocusContext.Specific;
    }
    else if ((0, trappable_1.hasGlobalFocus)() && !(0, trappable_1.isGlobalFocusLoading)()) {
        return FocusContext.Global;
    }
    else {
        return FocusContext.None;
    }
};
exports.getFocusContext = getFocusContext;
/**
 * Focuses FocusContainers that now have focus and blurs FocusContainers
 * that no longer have focus
 *
 * @param nextActiveContainers the list of FocusContainers that will have focus
 * after the operation
 */
const updateContainerFocus = () => {
    const nextActiveContainers = (0, dependencyGraph_1.getIndependentContainers)();
    for (const focusContainer of new Set([...activeContainers, ...nextActiveContainers])) {
        const inCurrentlyActiveContainers = activeContainers.includes(focusContainer);
        const inNextActiveContainers = nextActiveContainers.includes(focusContainer);
        if (inCurrentlyActiveContainers && !inNextActiveContainers) {
            focusContainer.onContainerBlur();
        }
        else if (!inCurrentlyActiveContainers && inNextActiveContainers) {
            focusContainer.onContainerFocus();
        }
    }
    activeContainers = nextActiveContainers;
    (0, focusManager_1.pruneCurrentlyFocusedInteractable)();
};
/**
 * Adds the focus container to the list of currently focused focus containers.
 *
 *  @param containerToAdd - the focus container to focus
 */
const addFocusContainer = (containerToAdd) => {
    if (focusedContainers.includes(containerToAdd)) {
        return false;
    }
    focusedContainers.push(containerToAdd);
    (0, dependencyGraph_1.addToDependencyGraph)(containerToAdd);
    updateContainerFocus();
};
exports.addFocusContainer = addFocusContainer;
/**
 * Sets the given container's focus based on existing state. If we previously
 * had global focus or the container specifies that it takes focus, shift focus to the container
 *
 *  @param containerToAdd - the focus container to focus
 */
const setContainerFocus = (containerToAdd) => {
    const currentlyFocusedInteractable = (0, focusManager_1.getCurrentlyFocusedInteractable)();
    const searchScope = containerToAdd.getSearchScope();
    if (!searchScope) {
        return;
    }
    if (containerToAdd.takesFocus() === types_1.TakesFocus.Forced ||
        (!currentlyFocusedInteractable && containerToAdd.takesFocus() === types_1.TakesFocus.FirstOnly)) {
        (0, initialFocusFinder_1.shiftFocus)(searchScope);
    }
    else if (!currentlyFocusedInteractable) {
        for (const focusContainer of (0, exports.getActiveContainers)()) {
            if (focusContainer.takesFocus() !== types_1.TakesFocus.None && !currentlyFocusedInteractable) {
                (0, initialFocusFinder_1.shiftFocus)(searchScope);
            }
        }
    }
};
exports.setContainerFocus = setContainerFocus;
/**
 * Removes the focus container from the list of currently focused focus containers and the stack of
 * focus containers with most recent focus, and then shifts focus to another container (or global focus)
 * if current focus was contained within the container
 *
 *  @param containerToRemove - the focus container to unfocus
 */
const removeFocusContainer = (containerToRemove) => {
    if (!focusedContainers.includes(containerToRemove)) {
        return;
    }
    focusedContainers = focusedContainers.filter((container) => container !== containerToRemove);
    (0, dependencyGraph_1.removeFromDependencyGraph)(containerToRemove);
    updateContainerFocus();
    if ((0, exports.getFocusContext)() === FocusContext.Global) {
        (0, initialFocusFinder_1.findInitialFocus)();
    }
    else {
        setTimeout(() => {
            if (!(0, focusManager_1.getCurrentlyFocusedInteractable)()) {
                for (const focusContainer of (0, exports.getActiveContainers)()) {
                    if (focusContainer.takesFocus() !== types_1.TakesFocus.None && !(0, focusManager_1.getCurrentlyFocusedInteractable)()) {
                        (0, initialFocusFinder_1.findInitialFocus)(false, focusContainer);
                    }
                }
            }
        }, 0);
    }
};
exports.removeFocusContainer = removeFocusContainer;
/**
 * Recalculates the dependency graph to account an updated FocusContainer zIndex
 *
 * @param container the container to reprocess in the dependency graph
 */
const handleFocusContainerZIndexChange = (container) => {
    (0, dependencyGraph_1.removeFromDependencyGraph)(container);
    (0, dependencyGraph_1.addToDependencyGraph)(container);
    updateContainerFocus();
    if (!(0, focusManager_1.getCurrentlyFocusedInteractable)()) {
        setTimeout(() => (0, initialFocusFinder_1.findInitialFocus)(), 0);
    }
};
exports.handleFocusContainerZIndexChange = handleFocusContainerZIndexChange;
/**
 * Returns whether the given element is in a valid focus context. An element is considered
 * in a valid focus context if
 *  1) The focus context is global && the element is not contained within a focus container
 *  2) The focus context is specific && the element is contained within an active focus container
 *  3) If a focus container is provided, the element must be contained within that focus container
 *
 * @param element The element to check
 * @param focusContainer Optional, the focus container to check
 * @returns whether the element is in a valid focus context
 */
const isElementInValidFocusContext = (element, focusContainer) => {
    if (!element) {
        return false;
    }
    const focusContext = (0, exports.getFocusContext)();
    if (focusContext === FocusContext.None) {
        return false;
    }
    const parentFocusContainer = (0, utils_1.getParentFocusContainer)(element);
    if (focusContext === FocusContext.Global) {
        if (focusContainer || parentFocusContainer) {
            return false;
        }
    }
    else {
        // If there the element has no parent focus container, or if the given focus container does not
        // match the provided focus container, or the parent focus container is not active, then the element
        // is not in an active focus context.
        if (!parentFocusContainer ||
            (focusContainer && focusContainer !== parentFocusContainer) ||
            !activeContainers.includes(parentFocusContainer)) {
            return false;
        }
    }
    return true;
};
exports.isElementInValidFocusContext = isElementInValidFocusContext;
//# sourceMappingURL=data:application/json;base64,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